import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import Form from "components/Form";
import { fetchDataFromApi } from "@Utils";
import GrievanceComment from "./Comment/GrievanceComment";
import {
  GRIEVANCE_URL,
  INTERNAL_GRIEVANCE_STATUS,
  URLS,
  GRIEVANCE_URGENCY_LEVELS,
  GRIEVANCE_TYPE,
  GRIEVANCE_ASSIGNEE,
  TRANSLATION_DISCLAIMER_MESSAGE,
} from "configs/constants";
import GrievanceTranslation from "./Translation/GrievanceTranslation";
import * as Yup from "yup";
import GrievanceClosedDetails from "./GrievanceClosedDetails";

const SUPPORTING_DOCUMENT_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/png",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const initialValues = {
  internal_status: "",
  supporting_document: null,
  remarks: "",
  urgency_level: "",
  grievance_type: "",
  assignee: [],
};

const validationSchema = Yup.object({
  supporting_document: Yup.mixed().test(
    "fileType",
    "Unsupported file type",
    (value) => {
      if (value === null) {
        return true;
      } else if (typeof value === "string") {
        return true;
      } else {
        return SUPPORTING_DOCUMENT_FORMATS.includes(value.type);
      }
    }
  ),
  internal_status: Yup.string()
  .required("Status is required")
  .typeError("Invalid Status"),
  assignee: Yup.string()
  .required("Assignee is required")
  .typeError("Invalid Assignee"),
  urgency_level: Yup.string()
  .required("Urgency Level is required")
  .typeError("Invalid Urgency Level"),
  grievance_type: Yup.string()
  .required("Grienvance Type By is required")
  .typeError("Invalid Grievance Level"),

});

const GrievanceEditForm = () => {
  const [grievanceChoices, setGrievanceChoices] = useState([]);

  const params = useParams();

  useEffect(() => {
    const config = {
      params: {
        grievance_id: params.id,
      },
    };
    fetchDataFromApi(
      INTERNAL_GRIEVANCE_STATUS,
      setGrievanceChoices,
      false,
      config
    );
  }, []);

  const formButtons = ["save", "cancel"];

  const formFields = [
    { type: "title", title: "Employee Details" },
    {
      type: "renderonly",
      label: "Employee ID",
      key: "user.employee_id",
    },
    { type: "renderonly", label: "Employee Name", key: "user.name" },
    { type: "renderonly", label: "Mobile Number", key: "user.mobile_number" },
    {
      type: "renderonly",
      label: "Supervisor",
      key: "user.supervisor",
    },
    {
      type: "renderonly",
      label: "Nationality",
      key: "user.nationality",
    },
    { type: "renderonly", label: "Company", key: "user.company" },
    { type: "renderonly", label: "Zone", key: "user.zone" },
    { type: "renderonly", label: "Region", key: "user.region" },
    { type: "renderonly", label: "Estate", key: "user.estate" },
    { type: "renderonly", label: "Division", key: "user.division" },
    { type: "renderonly", label: "Block", key: "user.block" },
    { type: "renderonly", 
      label: "Is Anonymous?", 
      key: "is_anonymous",
      valueType: "boolean",
      yesValue: "Yes",
      noValue: "No",
      supportedTypes: ["Anonymous Information is hidden if your Role has no access to Anonymous details"],
      infobutton: true,

    },

    { type: "title", title: "Grievance Details" },
    { type: "renderonly", label: "Grievance ID", key: "grievance_id" },
    {
      type: "renderonly",
      label: "Date and Time",
      key: "created_at",
      valueType: "date_time_origin",
    },
    {
      type: "renderonly",
      label: "Mobile Date and Time",
      key: "mobile_date_time",
      valueType: "date_time_origin",
    },
    {
      type: "renderonly",
      label: "Type",
      key: "is_audio_message",
      valueType: "boolean",
      yesValue: "Audio",
      noValue: "Text",
    },
    {
      type: "renderonly",
      label: "Audio",
      key: "audio_file",
      valueType: "audioLink",
      linkWrapper: "grievance_id",
      linkWrapperText: "Audio-Link",
      renderKey: "is_audio_message",
      renderValue: true,
    },
    {
      type: "renderonly",
      label: "Grievance Text",
      key: "text_message",
      renderKey: "is_audio_message",
      renderValue: false,
      gridSize: { xs: 12 },
    },
    {
      type: "reactselect",
      name: "internal_status",
      required:true,
      label: "Status",
      outerOptions: grievanceChoices,
      gridSize: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 4,
      },
    },
    {
      type: "fileupload",
      name: "supporting_document",
      label: "Supporting Document",
      supportedTypes: ["png", "jpg", "gif", "pdf", "doc", "docx"],
      gridSize: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 4,
      },
    },
    {
      type: "reactselect",
      name: "assignee",
      label: "Assignee",
      required:true,
      proxyUrl: GRIEVANCE_ASSIGNEE,
      paginated: false,
      gridSize: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 4,
      },
    },
    {
      type: "reactselect",
      name: "urgency_level",
      label: "Urgency Level",
      required: true,
      proxyUrl: GRIEVANCE_URGENCY_LEVELS,
      paginated: true,
      gridSize: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 4,
      },
    },
    {
      type: "reactselect",
      name: "grievance_type",
      required: true,
      label: "Grievance Type",
      proxyUrl: GRIEVANCE_TYPE,
      paginated: true,
      gridSize: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 4,
      },
    },
    {
      type: "input",
      size: "medium",
      name: "remarks",
      label: "Remarks",
      variant: "outlined",
      multiline: true,
      rows: 3,
      gridSize: { xs: 12, sm: 12 },
    },

    {
      type: "title",
      title: "Closed Details",
      renderKey: "internal_status",
      renderWithMultipleValuesOfSameKey: ["Pending Close", "Closed"],
      renderWithUnchangeableState: true,
    },
    {
      type: "component",
      component: GrievanceClosedDetails,
      gridSize: { xs: 12 },
      renderKey: "internal_status",
      renderWithMultipleValuesOfSameKey: ["Pending Close", "Closed"],
      renderWithUnchangeableState: true,
      passValueWithKey: [
        "pending_close_by_name",
        "pending_close_at",
        "closed_by_name",
        "closed_at",
      ],
    },

    {
      type: "title",
      title: "Translation Details",
      showInfoIcon: true,
      infoContent: TRANSLATION_DISCLAIMER_MESSAGE,
    },
    {
      type: "component",
      component: GrievanceTranslation,
      passValueWithKey: [
        "allowed_translation_count",
        "translated_text",
        "internal_status",
      ],
      gridSize: { xs: 12 },
    },

    { type: "title", title: "Comment" },
    {
      type: "component",
      component: GrievanceComment,
      key: "comments",
      passValueWithKey: "user_language",
      gridSize: { xs: 12 },
    },
  ];

  return (
    <>
      <Form
        initialValues={initialValues}
        buttonPosition={26}
        validationSchema={validationSchema}
        submitUrl={GRIEVANCE_URL}
        formFields={formFields}
        listRoute={URLS.GRIEVANCE_LIST}
        formButtons={formButtons}
        formTitle="Grievance"
        noListButton
        isMultipart
        outerConfig={{ params: { bo_request: true } }}
      />
    </>
  );
};

export default GrievanceEditForm;
